import { createFileRoute } from "@tanstack/react-router";
import { ReadOnlyPill } from "@/components/auth/ReadOnlyPill";
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useFinanceKpis } from "@/features/finance/api";
import { currentFy, fyOptions } from "@/features/finance/fy";
import { inr } from "@/features/finance/format";
import { TrendingUp, TrendingDown, Wallet, Banknote, ShoppingBag, Calculator } from "lucide-react";
import { PendingActions } from "@/features/finance/dashboard/PendingActions";
import { InsuranceRisk } from "@/features/finance/dashboard/InsuranceRisk";
import { FarPreview } from "@/features/finance/dashboard/FarPreview";
import { DepreciationSummary } from "@/features/finance/dashboard/DepreciationSummary";
import { DisposalAccounting } from "@/features/finance/dashboard/DisposalAccounting";
import { QuickLinks } from "@/features/finance/dashboard/QuickLinks";
import { useBranch } from "@/context/BranchContext";

export const Route = createFileRoute("/_authenticated/finance/dashboard")({
  component: FinanceDashboard,
});

function FinanceDashboard() {
  const [fy, setFy] = useState(currentFy());
  const { currentBranchId } = useBranch();
  const { data: k, isLoading } = useFinanceKpis(fy, currentBranchId);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-semibold tracking-tight">Finance Workbench</h1>
            <ReadOnlyPill module="finance" />
          </div>
          <p className="text-sm text-muted-foreground">
            Capitalisation, fixed asset register, depreciation runs, disposal accounting and insurance risk actions.
          </p>
        </div>
        <Select value={fy} onValueChange={setFy}>
          <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
          <SelectContent>
            {fyOptions().map((y) => (
              <SelectItem key={y} value={y}>FY {y}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
        <Kpi label="Gross Block" value={inr(k?.gross_block ?? 0)} icon={<Wallet className="h-5 w-5 text-primary" />} />
        <Kpi label="Accumulated Dep" value={inr(k?.accumulated_dep ?? 0)} icon={<TrendingDown className="h-5 w-5 text-orange-500" />} />
        <Kpi label="Closing NBV" value={inr(k?.nbv ?? 0)} icon={<Banknote className="h-5 w-5 text-emerald-600" />} />
        <Kpi label={`Depreciation FY ${fy}`} value={inr(k?.dep_this_fy ?? 0)} icon={<Calculator className="h-5 w-5 text-amber-500" />} />
        <Kpi label={`Capex FY ${fy}`} value={inr(k?.capex_fy ?? 0)} icon={<TrendingUp className="h-5 w-5 text-blue-500" />} />
        <Kpi label={`Disposals FY ${fy}`} value={inr(k?.disposals_fy ?? 0)} icon={<ShoppingBag className="h-5 w-5 text-muted-foreground" />} />
      </div>
      {isLoading && <p className="text-sm text-muted-foreground">Loading KPIs...</p>}

      <PendingActions fy={fy} />
      <InsuranceRisk />
      <FarPreview fy={fy} />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <DepreciationSummary fy={fy} />
        <QuickLinks />
      </div>

      <DisposalAccounting fy={fy} />
    </div>
  );
}

function Kpi({ label, value, icon }: { label: string; value: string; icon: React.ReactNode }) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center gap-3 space-y-0 pb-2">
        <div className="grid h-9 w-9 place-items-center rounded-md bg-accent/30">{icon}</div>
        <CardTitle className="text-xs font-medium text-muted-foreground">{label}</CardTitle>
      </CardHeader>
      <CardContent className="text-lg font-semibold">{value}</CardContent>
    </Card>
  );
}
